import { hiddenCharacterPattern } from "@smartmanager/shell-ui/shared/text/hiddenCharacters";

/** Named HTML entities SmartManager decodes in provider text (lowercase keys). */
export const HTML_NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  apos: "'",
  bull: "•",
  copy: "©",
  deg: "°",
  emsp: " ",
  euro: "€",
  ensp: " ",
  gt: ">",
  hairsp: " ",
  hellip: "…",
  laquo: "«",
  ldquo: "“",
  lrm: "\u200e",
  lsquo: "‘",
  lt: "<",
  mdash: "—",
  middot: "·",
  nbsp: " ",
  ndash: "–",
  quot: '"',
  raquo: "»",
  rdquo: "”",
  reg: "®",
  rlm: "\u200f",
  rsquo: "’",
  shy: "\u00ad",
  thinsp: " ",
  times: "×",
  trade: "™",
  zerowidthspace: "\u200b",
  zwj: "\u200d",
  zwnj: "\u200c",
};

const MAX_ENTITY_DECODE_PASSES = 4;

/** One numeric character reference; invalid or surrogate values decode to "". */
export function decodeHtmlCodePoint(value: number): string {
  // Lone surrogates and out-of-range values throw; drop them instead.
  if (!Number.isFinite(value) || value < 0 || value > 0x10ffff) return "";
  if (value >= 0xd800 && value <= 0xdfff) return "";
  return String.fromCodePoint(value);
}

function decodeEntities(value: string): string {
  let decoded = value;
  for (let pass = 0; pass < MAX_ENTITY_DECODE_PASSES; pass += 1) {
    const next = decoded
      .replace(
        /&#x([0-9a-f]+);/gi,
        (_, hex) => decodeHtmlCodePoint(Number.parseInt(hex, 16)),
      )
      .replace(/&#(\d+);/g, (_, decimal) => decodeHtmlCodePoint(Number(decimal)))
      .replace(/&([a-z]+);/gi, (match, name) =>
        HTML_NAMED_ENTITIES[String(name).toLowerCase()] ?? match,
      );
    if (next === decoded) break;
    decoded = next;
  }
  return decoded;
}

/**
 * Line and paragraph separators plus every character that draws nothing
 * (soft hyphen, zero-width marks, bidi controls, tags; see hiddenCharacters).
 * Signs that do draw stay: the Arabic number signs and whole England,
 * Scotland and Wales flags.
 */
const HIDDEN_DISPLAY_CHARACTERS = hiddenCharacterPattern(String.raw`\u2028\u2029`, { keepVisibleFormat: true });

/**
 * Converts HTML-ish provider content into compact plain text for a text sink.
 *
 * Raw tags are removed before bounded entity decoding. That ordering means an
 * encoded tag can become visible text, but can never become markup here. Keep
 * the result in a React/text node; this is not an HTML-sanitization API.
 */
export function normalizePlainTextForDisplay(value: unknown): string {
  if (typeof value !== "string" && typeof value !== "number") return "";

  return decodeEntities(
    String(value)
      .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, " ")
      .replace(/<[^>]+>/g, " "),
  )
    .replace(HIDDEN_DISPLAY_CHARACTERS, "")
    .replace(/\u00a0/g, " ")
    // Older normalized summaries could be length-limited in the middle of a
    // numeric entity (for example `&#8204;` becoming `&#8`). That fragment is
    // provider padding, not readable source text.
    .replace(/\s*&#(?:x[0-9a-f]*|\d*)$/i, "")
    .replace(/\s+/g, " ")
    .trim();
}
